import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, FileSpreadsheet, Inbox, Loader2, Printer, Send, Undo2 } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button, buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import { pcmpUrgentBatchQueryOptions } from '../api/pcmpRequests.api';
import { downloadPcmpUrgentBatchXlsx } from '../api/pcmpUrgent.api';
import { PcmpBatchStatusPill } from '../components/PcmpMisBatches/PcmpMisBatchCells';
import { PcmpSelectionBar } from '../components/PcmpBatch/PcmpSelectionBar';
import { PcmpQueueFilterBar } from '../components/PcmpQueue/PcmpQueueFilterBar';
import { pcmpSelectColumn } from '../components/PcmpQueue/pcmpSelectColumn';
import { getPcmpRequestColumns } from '../components/PcmpRequests/PcmpRequestColumns';
import { PcmpRequestRowActions } from '../components/PcmpRequests/PcmpRequestRowActions';
import { PcmpUrgentPublishDialog } from '../components/PcmpUrgent/PcmpUrgentPublishDialog';
import {
  PcmpUrgentRollbackDialog,
  type PcmpUrgentRollbackTarget,
} from '../components/PcmpUrgent/PcmpUrgentRollbackDialog';
import { PCMP_CHANNEL_LABELS } from '../constants/pcmpProposal.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../constants/pcmpQueue.constants';
import { useDownloadPcmpBatchXlsx } from '../hooks/useDownloadPcmpBatchXlsx';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../hooks/usePcmpRowSelection';
import { usePcmpUrgentBatchFilters } from '../hooks/usePcmpUrgentBatchFilters';
import type { PcmpUrgentBatchItem } from '../types/pcmpUrgent.types';

/**
 * One urgent batch: its facts and the proposals the viewer may see in it.
 * Approvers also publish or roll back a staged batch and download its report.
 */
export function PcmpUrgentBatchPage() {
  const router = useRouter();
  const controller = usePcmpUrgentBatchFilters();
  const { batchNo, params, appliedFilters, limit } = controller;
  const { can } = usePcmpPermissions();
  const canManage = can(PCMP_PERMISSION.REQUEST_APPROVE, PCMP_PERMISSION.ACT);
  const canExport = can(PCMP_PERMISSION.REQUEST_APPROVE, PCMP_PERMISSION.EXPORT);

  const [publishing, setPublishing] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<PcmpUrgentRollbackTarget | null>(null);
  const selection = usePcmpRowSelection(JSON.stringify([batchNo, appliedFilters]));
  const xlsx = useDownloadPcmpBatchXlsx((date) => downloadPcmpUrgentBatchXlsx(batchNo, date));

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpUrgentBatchQueryOptions(batchNo, params),
  );

  useKeepPageInRange(params.page, data?.proposals.meta.totalPages, controller.setPage);

  const rows = useMemo(() => data?.proposals.items ?? [], [data]);
  const batch = data?.batch;
  const isStaged = batch?.status === 'STAGED';
  const isPublished = batch?.status === 'PUBLISHED';
  const canChange = canManage && isStaged;
  const canRollbackPublished = canManage && isPublished && (data?.batchHasRollbackItems ?? false);
  const canRollback = canChange || canRollbackPublished;

  const rollbackBlockedReason = useCallback(
    (item: PcmpUrgentBatchItem) =>
      item.canRollback ? null : (item.rollbackBlockedReason ?? 'Cannot be rolled back.'),
    [],
  );

  const openProposal = useCallback(
    (item: PcmpUrgentBatchItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const backButton = canManage ? (
    <Link
      to="/pcmp/requests/requested"
      search={{ tab: 'BATCHES' }}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
    >
      <ArrowLeft className="size-3.5" />
      Batches
    </Link>
  ) : (
    <Link
      to="/pcmp/requests"
      search={{ tab: 'BATCHES' }}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
    >
      <ArrowLeft className="size-3.5" />
      Batches
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageLoader />
      </div>
    );
  }

  if (error || !data || !batch) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle={`Could not load urgent batch #${batchNo}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  const columns = getPcmpRequestColumns<PcmpUrgentBatchItem>({
    leading: canRollback
      ? [
          pcmpSelectColumn<PcmpUrgentBatchItem>({
            selection,
            ...(canRollbackPublished ? { blockedReason: rollbackBlockedReason } : {}),
          }),
        ]
      : [],
    renderActions: (item) => <PcmpRequestRowActions item={item} />,
  });

  const summary = selection.summarize(0, 0);
  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;
  const startRollback = () =>
    setRollbackTarget({
      batchNo,
      proposalIds: selection.pickedIds,
      selectedAmount: summary.amount,
      wholeCount: batch.wholeBatchItemCount,
      isPublished: isPublished ?? false,
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {backButton}
          <h1 className="text-2xl font-semibold">Urgent Batch #{batch.batchNo}</h1>
          <PcmpBatchStatusPill status={batch.status} />
          {batch.channel ? (
            <span className="text-sm text-muted-foreground">
              {PCMP_CHANNEL_LABELS[batch.channel]} pays
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canExport ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => xlsx.mutate()}
                disabled={xlsx.isPending}
                className="gap-1.5"
              >
                {xlsx.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="size-3.5" />
                )}
                Excel
              </Button>
              <Link
                to="/pcmp/requests/batches/$batchNo/print"
                params={{ batchNo }}
                target="_blank"
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
              >
                <Printer className="size-3.5" />
                Print / PDF
              </Link>
            </>
          ) : null}
          {canChange ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setPublishing(true)}
              disabled={batch.wholeBatchItemCount === 0}
              className="gap-1.5"
            >
              <Send className="size-3.5" />
              Publish
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <div className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">Proposals in the batch</span>
          <span className="text-lg font-semibold tabular-nums">
            {formatNumber(batch.wholeBatchItemCount)}
          </span>
        </div>
        <div className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">You can see</span>
          <span className="text-lg font-semibold tabular-nums">
            {formatNumber(data.proposals.meta.total)}
          </span>
        </div>
        <div className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">Published</span>
          <span className="text-sm font-medium">
            {batch.publishedAt ? formatDisplayDateTime(batch.publishedAt) : 'Not yet'}
          </span>
        </div>
      </div>

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-urgent-batch" />

      {canRollback ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          hint={
            isPublished
              ? 'Tick published proposals to roll them back to MIS Pre-Batch.'
              : 'Tick proposals to roll them back to Urgent Pre-Batch, or roll back the whole batch.'
          }
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={startRollback}
            disabled={isPublished ? summary.count === 0 : batch.wholeBatchItemCount === 0}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            <Undo2 className="size-3.5" />
            {isPublished ? 'Rollback Selected' : 'Roll back'}
          </Button>
        </PcmpSelectionBar>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={false}
        stickyFirstColumn
        className={cn(isFetching && 'opacity-70')}
        emptyIcon={Inbox}
        emptyMessage={
          batch.wholeBatchItemCount === 0
            ? 'This batch is empty'
            : 'No proposals in this batch match'
        }
        {...(batch.wholeBatchItemCount === 0 && isStaged
          ? { emptyHint: 'Stage proposals from Pre-Batch into it, or leave it open.' }
          : {})}
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(item) =>
          canRollback && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
        }
        onRowClick={openProposal}
      />

      <DataTablePagination
        page={data.proposals.meta.page}
        totalPages={data.proposals.meta.totalPages}
        total={data.proposals.meta.total}
        limit={data.proposals.meta.limit}
        onPageChange={controller.setPage}
        limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
      />

      {publishing ? (
        <PcmpUrgentPublishDialog
          batchNo={batchNo}
          itemCount={batch.wholeBatchItemCount}
          onClose={() => setPublishing(false)}
        />
      ) : null}
      {rollbackTarget ? (
        <PcmpUrgentRollbackDialog
          target={rollbackTarget}
          refNoOf={refNoOf}
          onRolledBack={selection.clear}
          onClose={() => setRollbackTarget(null)}
        />
      ) : null}
    </div>
  );
}
